// Admin › Settings › Referral Program › Ledger — reward credit movements (part 4).
// The newest grants and take-backs across all wallets, with what is left of
// each grant and when it expires. Part 6: give credit by hand and take back the
// unspent part of a grant, always with a reason.

import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Plus } from 'lucide-react'
import {
  costReportKey,
  fetchRewardsLedger,
  grantManualCredit,
  ledgerKey,
  takeBackCredit,
  type RewardsLedgerRow,
} from '../../../api/referralRewards'
import { OrgSearchModal } from '../../../components/OrgSearchModal'
import { Badge } from '../../../components/ui/badge'
import { Button } from '../../../components/ui/button'
import { Card } from '../../../components/ui/card'
import { DataTableShell } from '../../../components/ui/data-table-shell'
import { DatePicker } from '../../../components/ui/date-picker'
import {
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../components/ui/dialog'
import { Dialog } from '../../../components/ui/dialog.lib'
import { Field } from '../../../components/ui/field'
import { Input } from '../../../components/ui/input'
import type { DirectoryOrg } from '../../../mocks/data/organizations'
import { useT } from '../../../i18n'
import { rewardsLedgerI18n, type RewardsLedgerStrings } from '../../../i18n/admin/rewards-referrals'
import { formatCurrency } from '../../../lib/currency'
import { formatDate, type PlainDate } from '../../../lib/datetime'

const TEXTAREA_CLASS =
  'w-full rounded-[var(--field-radius)] border border-[var(--field-border)] bg-[var(--field-bg)] px-[var(--field-padding-x)] py-2 text-[length:var(--field-font-size)] text-[var(--field-text)] placeholder:text-[var(--field-placeholder)] focus-visible:border-[var(--field-focus-border)] focus-visible:outline-none focus-visible:shadow-[var(--field-focus-shadow)]'

function fmtDate(iso: string): string {
  return formatDate(iso, { year: 'numeric', month: 'short', day: 'numeric' })
}

function usd(value: string | number): string {
  return formatCurrency(Number(value), 'USD')
}

function ReasonField({
  id,
  reason,
  setReason,
  touched,
  setTouched,
  t,
}: {
  id: string
  reason: string
  setReason: (v: string) => void
  touched: boolean
  setTouched: (v: boolean) => void
  t: RewardsLedgerStrings
}) {
  const ok = reason.trim().length >= 3
  return (
    <Field id={id} label={t.reasonLabel} required error={touched && !ok ? t.reasonRequired : undefined}>
      <textarea
        rows={3}
        maxLength={500}
        className={TEXTAREA_CLASS}
        placeholder={t.reasonPlaceholder}
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        onBlur={() => setTouched(true)}
      />
    </Field>
  )
}

function useRefreshLedger() {
  const queryClient = useQueryClient()
  return async () => {
    await queryClient.invalidateQueries({ queryKey: ledgerKey })
    await queryClient.invalidateQueries({ queryKey: costReportKey })
  }
}

function GrantDialog({ onClose, t }: { onClose: () => void; t: RewardsLedgerStrings }) {
  const refresh = useRefreshLedger()
  const [org, setOrg] = useState<DirectoryOrg | null>(null)
  const [picking, setPicking] = useState(false)
  const [amount, setAmount] = useState('')
  const [expiresOn, setExpiresOn] = useState<PlainDate | null>(null)
  const [reason, setReason] = useState('')
  const [touched, setTouched] = useState(false)
  const value = Number(amount)
  const amountOk = /^\d+(\.\d{1,2})?$/.test(amount.trim()) && value > 0 && value <= 10000
  const ok = org !== null && amountOk && reason.trim().length >= 3
  const mutation = useMutation({
    mutationFn: () =>
      grantManualCredit({
        org_id: org!.orgId,
        amount_usd: amount.trim(),
        reason: reason.trim(),
        expires_on: expiresOn,
      }),
    onSuccess: async () => {
      await refresh()
      onClose()
    },
  })
  return (
    <>
      <Dialog open={!picking} onOpenChange={(open) => !open && onClose()}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t.grantTitle}</DialogTitle>
            <DialogDescription>{t.grantBody}</DialogDescription>
          </DialogHeader>
          <div className="space-y-1">
            <div className="flex flex-wrap items-center gap-2">
              {org ? (
                <span className="text-sm font-medium text-[var(--color-text)]">{org.orgName}</span>
              ) : null}
              <Button variant="outline" size="sm" onClick={() => setPicking(true)}>
                {org ? t.changeSeller : t.pickSeller}
              </Button>
            </div>
            {touched && org === null && (
              <p className="text-sm text-[var(--color-danger)]">{t.sellerRequired}</p>
            )}
          </div>
          <Field
            id="rewards-manual-amount"
            label={t.amountLabel}
            required
            error={touched && !amountOk ? t.amountInvalid : undefined}
          >
            <Input
              inputMode="decimal"
              placeholder="50.00"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
            />
          </Field>
          <div className="space-y-1">
            <DatePicker label={t.expiresLabel} value={expiresOn} onChange={setExpiresOn} />
            <p className="text-xs text-[var(--color-text-muted)]">{t.expiresHint}</p>
          </div>
          <ReasonField
            id="rewards-manual-reason"
            reason={reason}
            setReason={setReason}
            touched={touched}
            setTouched={setTouched}
            t={t}
          />
          {mutation.isError && (
            <p role="alert" className="text-sm text-[var(--color-danger)]">
              {(mutation.error as Error).message}
            </p>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={onClose}>
              {t.cancel}
            </Button>
            <Button
              disabled={mutation.isPending}
              onClick={() => {
                setTouched(true)
                if (ok) mutation.mutate()
              }}
            >
              {mutation.isPending ? t.saving : t.grantSubmit}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <OrgSearchModal
        side={picking ? 'seller' : null}
        open={picking}
        onClose={() => setPicking(false)}
        onSelect={(picked) => {
          setOrg(picked)
          setPicking(false)
        }}
        actionLabel={t.pickerAction}
        description={t.pickerDescription}
      />
    </>
  )
}

function TakeBackDialog({
  row,
  onClose,
  t,
}: {
  row: RewardsLedgerRow
  onClose: () => void
  t: RewardsLedgerStrings
}) {
  const refresh = useRefreshLedger()
  const [reason, setReason] = useState('')
  const [touched, setTouched] = useState(false)
  const mutation = useMutation({
    mutationFn: () => takeBackCredit(row.wallet_event_id, reason.trim()),
    onSuccess: async () => {
      await refresh()
      onClose()
    },
  })
  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t.takeBackTitle(row.org_name, usd(row.remaining_usd ?? 0))}</DialogTitle>
          <DialogDescription>{t.takeBackBody}</DialogDescription>
        </DialogHeader>
        <ReasonField
          id="rewards-take-back-reason"
          reason={reason}
          setReason={setReason}
          touched={touched}
          setTouched={setTouched}
          t={t}
        />
        {mutation.isError && (
          <p role="alert" className="text-sm text-[var(--color-danger)]">
            {(mutation.error as Error).message}
          </p>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            {t.cancel}
          </Button>
          <Button
            variant="destructive"
            disabled={mutation.isPending}
            onClick={() => {
              setTouched(true)
              if (reason.trim().length >= 3) mutation.mutate()
            }}
          >
            {mutation.isPending ? t.saving : t.takeBack}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export function RewardsLedgerPage() {
  const t = useT(rewardsLedgerI18n)
  const query = useQuery({ queryKey: ledgerKey, queryFn: fetchRewardsLedger })
  const rows = query.data?.items ?? []
  const [granting, setGranting] = useState(false)
  const [takingBack, setTakingBack] = useState<RewardsLedgerRow | null>(null)

  return (
    <Card className="space-y-3 p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-base font-semibold text-[var(--color-text)]">{t.title}</h2>
          <p className="mt-0.5 text-sm text-[var(--color-text-muted)]">{t.subtitle}</p>
        </div>
        <Button size="sm" onClick={() => setGranting(true)}>
          <Plus className="h-4 w-4" aria-hidden="true" />
          {t.grantButton}
        </Button>
      </div>
      {query.isLoading ? (
        <p className="text-sm text-[var(--color-text-muted)]">{t.loading}</p>
      ) : query.isError ? (
        <div className="flex flex-wrap items-center gap-3 text-sm">
          <span role="alert" className="text-[var(--color-danger)]">
            {t.loadError}
          </span>
          <Button variant="outline" size="sm" onClick={() => void query.refetch()}>
            {t.retry}
          </Button>
        </div>
      ) : rows.length === 0 ? (
        <p className="text-sm text-[var(--color-text-muted)]">{t.empty}</p>
      ) : (
        <DataTableShell variant="embedded">
          <table>
            <thead>
              <tr>
                <th>{t.columns.date}</th>
                <th>{t.columns.seller}</th>
                <th>{t.columns.movement}</th>
                <th className="text-right">{t.columns.amount}</th>
                <th>{t.columns.expires}</th>
                <th className="text-right">{t.columns.left}</th>
                <th>
                  <span className="sr-only">{t.takeBack}</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.wallet_event_id}>
                  <td className="tabular-nums">{fmtDate(row.created_at)}</td>
                  <td className="font-medium">{row.org_name}</td>
                  <td>
                    <Badge variant={row.kind === 'granted' ? 'success' : 'warning'}>
                      {t.kinds[row.kind]}
                    </Badge>
                    {row.source && (
                      <span className="ml-2 text-xs text-[var(--color-text-muted)]">
                        {t.sources[row.source] ?? row.source}
                      </span>
                    )}
                  </td>
                  <td className="text-right tabular-nums">{usd(row.amount_usd)}</td>
                  <td className="tabular-nums">
                    {row.kind === 'granted' ? (row.expires_at ? fmtDate(row.expires_at) : t.never) : '—'}
                  </td>
                  <td className="text-right tabular-nums">
                    {row.remaining_usd == null ? '—' : usd(row.remaining_usd)}
                  </td>
                  <td className="text-right">
                    {row.kind === 'granted' && Number(row.remaining_usd ?? 0) > 0 && (
                      <Button variant="outline" size="sm" onClick={() => setTakingBack(row)}>
                        {t.takeBack}
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </DataTableShell>
      )}
      {granting && <GrantDialog onClose={() => setGranting(false)} t={t} />}
      {takingBack && <TakeBackDialog row={takingBack} onClose={() => setTakingBack(null)} t={t} />}
    </Card>
  )
}
